import { Directive, Input, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef, OverlayPositionBuilder } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TooltipComponent } from '../components/tooltip/tooltip.component';

@Directive({
  selector: '[customTooltip]',
  standalone: false
})
export class CustomTooltipDirective implements OnDestroy {
  @Input('customTooltip') tooltipText: string | null = '';
  @Input() tooltipDisabled: boolean = false;
  @Input() tooltipShowDelay: number = 500;
  @Input() tooltipHideDelay: number = 0;
  @Input() tooltipClass: string = '';
  @Input() tooltipPosition: 'auto' | 'below' | 'above' | 'left' | 'right' = 'auto';

  private overlayRef: OverlayRef | null = null;
  private showTimeout: any;
  private hideTimeout: any;

  constructor(
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef
  ) {}

  @HostListener('mouseenter')
  onMouseEnter() {
    if (this.tooltipDisabled || !this.tooltipText) {
      return;
    }

    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    this.showTimeout = setTimeout(() => {
      this.show();
    }, this.tooltipShowDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }

    if (this.overlayRef) {
      this.hideTimeout = setTimeout(() => {
        this.hide();
      }, this.tooltipHideDelay);
    }
  }

  /** Mobile/touch fallback only: there's no hover, so tap toggles the tooltip.
   *  Gated on the *event's own* `pointerType`, not a device-capability media query —
   *  `(hover: none)` can be true on hybrid devices (e.g. a touchscreen laptop) even
   *  while the user is clicking with a mouse, which wrongly made a plain mouse click
   *  toggle/close the tooltip on desktop. A mouse/pen click here is a no-op, same as
   *  before this fallback existed. stopPropagation keeps the immediate document click
   *  listener (below) from closing it right back on the same tap. */
  @HostListener('click', ['$event'])
  onClick(event: PointerEvent) {
    if (this.tooltipDisabled || !this.tooltipText || !this.isTouchTap(event)) {
      return;
    }
    event.stopPropagation();

    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    if (this.overlayRef) {
      this.hide();
      this.openedByTap = false;
    } else {
      this.show();
      this.openedByTap = true;
    }
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.overlayRef && this.openedByTap) {
      this.hide();
      this.openedByTap = false;
    }
  }

  private openedByTap = false;

  /** True click-from-touch signal wins. `pointerType` is unset on the rare browser
   *  that doesn't synthesize click as a PointerEvent — only then fall back to a
   *  device-level hover check. */
  private isTouchTap(event: PointerEvent): boolean {
    if (event.pointerType) {
      return event.pointerType === 'touch' || event.pointerType === 'pen';
    }
    return typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;
  }

  private show() {
    if (this.overlayRef) {
      return;
    }

    const positionBelow: ConnectedPosition = { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 };
    const positionAbove: ConnectedPosition = { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 };
    const positionRight: ConnectedPosition = { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 };
    const positionLeft: ConnectedPosition = { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 };

    const positionMap: Record<string, ConnectedPosition[]> = {
      below: [positionBelow, positionAbove],
      above: [positionAbove, positionBelow],
      right: [positionRight, positionLeft],
      left:  [positionLeft, positionRight],
      auto:  [positionRight, positionLeft, positionAbove, positionBelow]
    };

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(16)
      .withPositions(positionMap[this.tooltipPosition] ?? positionMap['auto']);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      panelClass: this.tooltipClass,
      scrollStrategy: this.overlay.scrollStrategies.close()
    });

    const tooltipPortal = new ComponentPortal(TooltipComponent);
    const tooltipRef = this.overlayRef.attach(tooltipPortal);
    tooltipRef.instance.text = this.tooltipText;
  }

  private hide() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.hide();
  }
}
